import React, { useContext, useEffect, useMemo, useState } from "react";
import InterfaceContext, { SupportedLanguage } from "./Context";
import { Button, DataTable } from "@aidkitorg/component-library";
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { Translations } from "@aidkitorg/i18n/lib";
import { useLocalizedStrings } from "./Localization";
import { caseStatuses, getStageFromRequest, SupportRequest, TableKey } from "./SupportUtil";
import { snakeToEnglish, classNames } from './Util';
import { Text } from "@aidkitorg/types/lib/survey";
import { SupportCaseBadge } from "./SupportCase";
import { GetOrCreateSupportCaseParams, SupportRow } from "./Support.types";
import { SupportTableFilters, SupportTableFilterOptions, TagOption, UserOption } from "./SupportTableV3Filters";

type ExtraColumnConfig = {
  targetField: string;
  filterable?: boolean;
  columnIndex?: number;
  consolidatedColumn?: {
    grouping: 'applicant_info' | 'case_info';
    label?: Text | 'hide';
    tailwindClass?: string;
  };
};

type ConsolidatedInfoItem = {
  value: string | null | undefined;
  label?: string;
  tailwindClass?: string;
  // on hover, show the title
  title?: string;
};

type SupportTableV3Props = {
  filter: string;
  data: SupportRequest[];
  channel: string;
  getOrCreateSupportCase: (row: GetOrCreateSupportCaseParams, leaveUnassigned?: boolean) => Promise<string>;
  makeSupportCaseUrl: (channel: string, caseId: string) => string;
  loading: boolean;
  extraColumns?: ExtraColumnConfig[];
};

function langForDate(lang: SupportedLanguage) {
  return lang.replaceAll('_', '-');
}

// Helpers to create local dates from date strings (YYYY-MM-DD format)
const createLocalStartOfDay = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0); // Start of day in local time
};
const createLocalEndOfDay = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999); // End of day in local time
};

// Check if a date is within a start/end range (inclusive)
const isDateInRange = (date: Date | null, start: Date | null, end: Date | null): boolean => {
  if (!date || (!start && !end)) return true;
  return (!start || date >= start) && (!end || date <= end);
};

const maskSystemUser = (row: { assigned_to_name?: string | null }, field: { key: 'assigned_to_name' }) => {
  const value = row[field.key] || '';
  if(value?.toLowerCase?.() === 'system' && field.key === 'assigned_to_name') {
    return '';
  }
  return value;
};

const localizeStageFn = (L: Translations) => (stage: string) => {
  switch (stage) {
    case "No Application":
      return L.application_stage.pre_application;
    case "In Progress":
      return L.application_stage.in_progress;
    case "Applied":
      return L.application_stage.applied;
    case "All Stages":
      return L.support.all_stages;
    default:
      return stage;
  }
};

const formatFieldName = (fieldName: string): string => {
  return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const concatExtraColumnValues = (row: any, extraColumns: ExtraColumnConfig[]): string => {
  return extraColumns
    .map(col => row.target_fields?.[col.targetField] ?? '')
    .filter(Boolean)
    .join(' ');
};

/**
 * Returns a new array: base + extra items derived from applicantInfos.
 * Extras with `columnIndex` are inserted relative to the base array, others are appended.
 */
export function mergeBaseAndExtraColumns(
  extraColumns: ExtraColumnConfig[],
  row: any,
  items: ConsolidatedInfoItem[],
  lang: SupportedLanguage = 'en',
): ConsolidatedInfoItem[] {
  const mergedItems = [...items];
  const applicantInfoValues = Object.values(row?.request?.applicantInfos ?? {});

  // Sort by columnIndex, undefined goes last
  const sortedExtras = [...extraColumns].sort((a, b) => {
    return (a.columnIndex ?? 999) - (b.columnIndex ?? 999);
  });

  for (const extraCol of sortedExtras) {
    // Build value from all applicantInfos for this field
    const extraValue = applicantInfoValues
      .map((info: any) => info?.[extraCol.targetField])
      .filter((v) => v != null && v !== '')
      .join(', ');

    if (!extraValue) continue;

    const item: ConsolidatedInfoItem = {
      label: extraCol.consolidatedColumn?.label === 'hide'
        ? undefined
        : extraCol.consolidatedColumn?.label?.[lang] || extraCol.consolidatedColumn?.label?.en || formatFieldName(extraCol.targetField),
      value: extraValue,
      tailwindClass: extraCol.consolidatedColumn?.tailwindClass,
    };
    // Insert at columnIndex or at the end
    mergedItems.splice(extraCol.columnIndex ?? mergedItems.length, 0, item);
  }

  return mergedItems;
}

const renderConsolidatedInfo = (items: ConsolidatedInfoItem[]) => {
  const validItems = items.filter(item => item.value != null && item.value !== '');
  if (validItems.length === 0) return null;
  
  return (
    <div className="space-y-2">
      {validItems.map((item, index) => (
        <div key={`${index}-${item.label}`} className="flex flex-col items-start">
          {item.label && <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            {item.label}
          </span>}
          <span className={classNames(item.tailwindClass || `font-medium text-gray-600`)}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const formatDateWithTimezone = (dateStr: string | null | undefined, lang: SupportedLanguage = 'en'): string | null => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString(langForDate(lang), { timeZoneName: 'short' });
}

type SupportTableV3Row = SupportRequest['row'] & {
  request: SupportRequest;
  id: string | null;
  unhandled_messages: string | null;
  applicant_stage: string;
  str_tags: string;
  str_contact_methods: string;
  case_created_formatted: string | null; // first time they messaged us regarding the most recent case -- formatted for display
  case_resolved_formatted: string | null; // when their most recent case was resolved -- formatted for display
  min_formatted: string | null; // first time they messaged us -- formatted for display
  max_formatted: string | null; // last time they messaged us -- formatted for display
  extraColumns: { [extraColumn: string]: string };
}

export function SupportTableV3({
  filter,
  data,
  channel,
  getOrCreateSupportCase,
  makeSupportCaseUrl,
  loading,
  extraColumns = []
}: SupportTableV3Props) {
  const L = useLocalizedStrings();
  const localizeStage = localizeStageFn(L);
  const context = useContext(InterfaceContext);

  const [filters, setFilters] = useState<SupportTableFilterOptions>({
    lastMessageStart: "",
    lastMessageEnd: "",
    caseResolvedStart: "",
    caseResolvedEnd: "",
    tags: "",
    contactMethod: "",
    assignedTo: ""
  });
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  // Transform data for display
  const transformedData = useMemo(() => 
    data.map(request => {
      const row = request.row;
      const app = request.row.source;
      
      // include extra column values from target_fields
      const extraFields: { [extraColumn: string]: string } = {};
      extraColumns.forEach(col => {
        extraFields[col.targetField] = row.target_fields?.[col.targetField] ?? '';
      });
      // extract last message text for search and display
      // if it's a call and we have a transcript, show the transcript
      const last_message_text = row.last_message_text === '[Call]' && row.last_call_transcript
        ? `TRANSCRIPT: ${row.last_call_transcript}`
        : row.last_message_text || '';
      
      return { 
        id: app,
        ...row, 
        extraColumns: extraFields,
        // Format dates for display
        case_created_formatted: formatDateWithTimezone(row.case_created, context.lang),
        case_resolved_formatted: formatDateWithTimezone(row.case_resolved, context.lang),
        min_formatted: formatDateWithTimezone(row.min, context.lang),
        max_formatted: formatDateWithTimezone(row.max, context.lang),
        last_message_text,
        assigned_to_name: maskSystemUser({ assigned_to_name: row.assigned_to_name }, { key: 'assigned_to_name' }),
        unhandled_messages: row.unhandled_count,
        applicant_stage: localizeStage(getStageFromRequest(request)),
        str_tags: Array.isArray(row.tags) ? row.tags.join(', ') : (row.tags || ''),
        str_contact_methods: Array.isArray(row.contact_methods) ? row.contact_methods.join(', ') : (row.contact_methods || ''),
        request
      } satisfies SupportTableV3Row;
    }), [data, context.lang, localizeStage]
  );

  // Split extra columns by type
  const extraApplicantColumns: ExtraColumnConfig[] = [];
  const extraCaseColumns: ExtraColumnConfig[] = [];
  for (const col of extraColumns) {
    if (col.consolidatedColumn?.grouping === 'case_info') {
      extraCaseColumns.push(col);
    } else {
      // applicant_info is the default grouping
      extraApplicantColumns.push(col);
    }
  }

  // Wrap for cells that may contain long, unbroken strings (like emails, URLs)
  // Inserts soft breaks only when content would overflow
  const WRAP_META = { cellProps: { className: 'whitespace-normal break-all' } };

  const AppInfoColumn: ColumnDef<SupportTableV3Row> = {
    accessorFn: (row) => {
      // Concatenate values from all columns into one string for searching
      const extraCols = concatExtraColumnValues(row, extraApplicantColumns);
      return `${row.source} ${row.str_contact_methods} ${extraCols}`;
    },
    header: L.support.fields.applicant_info,
    size: 150,
    cell: ({ row }) => {
      const hasAppInfo = Object.values(row?.original?.request?.applicantInfos ?? {}).length > 0;
      const baseApplicantInfos: ConsolidatedInfoItem[] = [
        { value: row.original.source, label: row.original.str_contact_methods === 'email' ? undefined : row.original?.str_contact_methods, tailwindClass: hasAppInfo ? 'text-muted text-xs' : undefined }
      ];
      const mergedApplicantInfos = mergeBaseAndExtraColumns(extraApplicantColumns, row.original, baseApplicantInfos, context.lang);
      return renderConsolidatedInfo(mergedApplicantInfos);
    },
    meta: WRAP_META
  }

  const CaseInfoColumn: ColumnDef<SupportTableV3Row> = {
    accessorFn: (row) => {
      // Concatenate values from all columns into one string for searching
      const extraCols = concatExtraColumnValues(row, extraCaseColumns);
      return `${row.support_case_status} ${row.applicant_stage} ${row.assigned_to_name} ${row.str_tags} ${extraCols}`;
    },
    header: L.support.fields.case_info,
    size: 150,
    cell: ({ row }) => {
      const baseCaseInfos: ConsolidatedInfoItem[] = [
        { label: row.original.str_tags ? 'Stage' : undefined, value: row.original.applicant_stage }, // only label if we also have tags.
        { label: 'Tags', value: row.original.str_tags },
      ];
      const mergedCaseInfos = mergeBaseAndExtraColumns(extraCaseColumns, row.original, baseCaseInfos, context.lang);
      return renderConsolidatedInfo(mergedCaseInfos);
    },
    meta: WRAP_META
  }

  const actionsColumn: ColumnDef<SupportTableV3Row> = {
    accessorKey: 'custom_actions',
    header: 'Actions',
    size: 150,
    cell: ({ row }) => {
      return <div className='flex flex-row gap-4'>
        <Button 
          size="sm"
          onClick={async () => {
            let caseId = await getOrCreateSupportCase(row.original, true);
            window.open(makeSupportCaseUrl(channel, caseId), '_blank');
          }}
          variant="secondary"
        >
          {L.support.view_case}
        </Button>
        {(!!row.original.support_case && row.original.assigned_to?.toLowerCase() !== 'system') 
          ? !!row.original.assigned_to_name &&
            <div className="flex flex-col">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">{L.support.fields.assigned_to_name}</div>
              <div>{row.original.assigned_to_name}</div>
            </div>
          : <Button
            size="sm"
            onClick={async () => { 
              const caseId = await getOrCreateSupportCase(row.original, false);
              window.open(makeSupportCaseUrl(channel, caseId), '_blank');
            }}
            variant="primary"
          >
            {L.dashboard.claim}
          </Button>}
      </div>
    }
  }

  // Create consolidated columns with custom cell rendering
  const consolidatedColumns: ColumnDef<SupportTableV3Row>[] = [
    actionsColumn,
    {
      accessorFn: (row) => {
        return `${row.support_case_status || ''} ${row.assigned_to_name || ''}`;
      },
      header: L.support.fields.support_case_status,
      size: 85,
      minSize: 85, maxSize: 85,
      cell: ({ row }) => {
        const { support_case_status, assigned_to, case_created } = row.original;
        // Hide badge for messages without a support case and unassigned open cases
        const shouldHideBadge = !support_case_status || (support_case_status === 'open' && assigned_to === 'system');
        return (
          <div>
            {!shouldHideBadge && (
              <SupportCaseBadge 
                title={case_created || ''} 
                status={support_case_status as typeof caseStatuses[number]} 
                disableLabel={true} 
              />
            )}
          </div>
        );
      }
    },
    CaseInfoColumn,
    AppInfoColumn,
    {
      accessorKey: 'unhandled_messages',
      header: L.support.fields.unhandled_messages,
      size: 85,
      minSize: 85,
      maxSize: 85,
    },
    {
      accessorKey: 'max',
      header: L.support.fields.max,
      size: 85,
      minSize: 85,
      maxSize: 85,
      cell: ({ row }) => {
        return row.original.max_formatted;
      },
      filterFn: (row, columnId, filterValue) => {
        const date = row.original.max ? new Date(row.original.max) : null;
        const start = filterValue?.start ? createLocalStartOfDay(filterValue.start) : null;
        const end = filterValue?.end ? createLocalEndOfDay(filterValue.end) : null;
        return isDateInRange(date, start, end);
      },
    } as ColumnDef<SupportTableV3Row>,
    ...(filter === 'resolved' ? [{
      accessorKey: 'case_resolved_formatted',
      header: L.support.fields.case_resolved,
      size: 85,
      minSize: 85,
      maxSize: 85,
      filterFn: (row, columnId, filterValue) => {
        const date = row.original.case_resolved ? new Date(row.original.case_resolved) : null;
        const start = filterValue?.start ? createLocalStartOfDay(filterValue.start) : null;
        const end = filterValue?.end ? createLocalEndOfDay(filterValue.end) : null;
        return isDateInRange(date, start, end);
      },
    } as ColumnDef<SupportTableV3Row>] : []),
    {
      accessorFn: (row: SupportTableV3Row) => {
        // Concatenate all messages and transcripts into one string for searching
        return [...row.messages || [], ...row.transcripts || []].join(' ');
      },
      header: L.support.fields.last_message_text,
      size: 150,
      cell: ({ row }) => {
        const messages = row.original.messages;
        if (!messages || messages.length === 0) return null;
        return (
          <div className="whitespace-pre-wrap break-words overflow-hidden line-clamp-3" title={row.original.last_message_text}>
            {row.original.last_message_text}
          </div>
        );
      },
      meta: WRAP_META
    },
    // Hidden columns for filtering
    {
      accessorKey: 'str_tags',
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const tags = String(row.getValue(columnId)).toLowerCase();
        return tags.includes(filterValue.toLowerCase());
      },
    },
    {
      accessorKey: 'str_contact_methods',
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const methods = String(row.getValue(columnId)).toLowerCase();
        return methods === filterValue.toLowerCase();
      },
    },
    {
      accessorKey: 'assigned_to_name',
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        if (filterValue === 'unassigned') {
          const assignedTo = String(row.getValue(columnId)).toLowerCase();
          return !assignedTo || assignedTo === 'system';
        }
        const assignedTo = String(row.getValue(columnId)).toLowerCase();
        return assignedTo.includes(filterValue.toLowerCase());
      },
      
    },
  ];

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  useEffect(() => {
    // Get unique tagsn for filter options
    const uniqueTags = new Set<string>();
    transformedData.forEach(row => {
      const tags = row.tags?.map(t => t.trim()) || [];
      tags.forEach(tag => tag && uniqueTags.add(tag));
    });
    
    const tagOptionsNew = [
      { value: '', label: L.support.filters.all_tags },
      ...Array.from(uniqueTags).sort().map(tag => ({
        value: tag,
        label: tag
      }))
    ];

    // Get unique assignees for filter options
    const uniqueUsers = new Set<string>();
    transformedData.forEach(row => {
      const user = row.assigned_to_name;
      if (user && user.toLowerCase() !== 'system') {
        uniqueUsers.add(user);
      }
    });
    
    const userOptionsNew = [
      { value: '', label: L.support.filters.all_assignees },
      { value: 'unassigned', label: L.support.filters.unassigned },
      ...Array.from(uniqueUsers).sort().map(user => ({
        value: user,
        label: user
      }))
    ];

    // Compare and update only if changed
    if (JSON.stringify(tagOptionsNew) !== JSON.stringify(tagOptions)) {
      setTagOptions(tagOptionsNew);
    }
    if (JSON.stringify(userOptionsNew) !== JSON.stringify(userOptions)) {
      setUserOptions(userOptionsNew);
    }
  }, [transformedData]);

  return (
    <DataTable
      columns={consolidatedColumns}
      data={transformedData}
      columnPinningState={{
        right: ['actions']
      }}
      initializing={loading}
      enableSearch={true}
      noDataMessage={L.support.no_requests_found}
      dataFilteredOutMessage={L.support.no_requests_found}
      activeFilters={activeFiltersCount}
      filters={(table) => (
        <SupportTableFilters
          table={table}
          filters={filters}
          setFilters={setFilters}
          filter={filter}
          L={L}
          tagOptions={tagOptions}
          userOptions={userOptions}
        />
      )}
      pageSize={50}
      hiddenCols={['str_tags', 'str_contact_methods', 'assigned_to_name']}
    />
  )
}
